import { getUserProducts } from '@/utils/authorization/user-products'
import { isUserAllowed } from '@/utils/authorization/page-access'
import { getDefaultRoute } from '@/utils/authorization/default-route-redirects'
import { useClearSession } from '@/utils/authorization/clear-session'
import { maLogger } from '@/base/logger'
import { RouteName } from '@/base/constants/route'

/**
 * It checks whether the user is authorized to access the relevant page.
 * While doing this, the "authorized" field in the "meta" field in the "to" object given as a parameter is checked.
 * User authorization status is set to the "userStore.authorize" property.
 * In all cases, the user is directed to the relevant page.
 *
 * @param {object} to - The destination route object.
 * @param {object} from - The current route object.
 * @param {Function} next - The 'next' function used for route navigation.
 * @return {Promise<Function<next | void>>} - Returns a function that removes the registered guard.
 */
export async function maAuthorizationGuard(to, from, next) {
  const { clearSession } = useClearSession()

  try {
    const products = await getUserProducts()
    const isAllowed = isUserAllowed({
      requiredAccess: to.meta.requiredAccess,
      userActiveProducts: products,
    })

    if (!isAllowed) {
      maLogger.warn('[maAuthorizationGuard] User is not allowed to access the page: ', to.name)
      const defaultRoute = getDefaultRoute({ to, products })
      return next({ name: defaultRoute, query: to?.query })
    }
  }
  catch (e) {
    maLogger.warn('[maAuthorizationGuard] Failed to load user products:', e)
    clearSession()
    return next({ name: RouteName.AUTH_LOGIN, query: { forwardTo: to.fullPath } })
  }

  next()
}
